import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from "react-router-dom";
import { Button, Checkbox, Col, Divider, Flex, InputNumber, Modal, Row, Select, Table, Typography, theme } from 'antd';

import { FilterOutlined, StarFilled } from '@ant-design/icons';

import useAdmin from "hooks/useAdmin";
import useUser from "hooks/useUser";
import { Loading } from 'components';

import StrategyManager from 'pages/Admin/strategies/StrategyManager';

const { useToken } = theme;
const { Text } = Typography;

const PAGE_SIZE = 10;

const coreDisplayFields = [
    { name: 'value', label: 'Value' },
    { name: 'weekPoints', label: 'Week Points' },
    { name: 'totalPoints', label: 'Total Points' },
    { name: 'selectedBy', label: 'Selected By' }
];

const Playerlist = ({size, playerData, isLoading, showPlayersAsLinks = true}) => {

    const { adminData } = useAdmin();
    const { userData } = useUser();
    const { token } = useToken();

    const summaryKey = userData?.isPremium ? 'advancedSummary' : 'summary';

    const strategyManager = new StrategyManager();
    const strategyMatchData = strategyManager.getMatchDataStrategy(adminData.getSport(), adminData.hasAutoUpdate());

    const userDisplayFields = strategyMatchData.getFieldsForUserDisplay(userData?.isPremium);
    const displayFields = [...coreDisplayFields, ...userDisplayFields];

    const getDefaultVisibleFields = () => displayFields.filter(field => ! field.hidden).map(field => field.name);

    const positions = adminData.getPositionNames();

    const [columns, setColumns] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [filtersAreOpen, setFiltersAreOpen] = useState(false);
    const [visibleFields, setVisibleFields] = useState(getDefaultVisibleFields);
    const [positionFilter, setPositionFilter] = useState([]);
    const [maxValueFilter, setMaxValueFilter] = useState();
    const [minInningsFilter, setMinInningsFilter] = useState();
    const [minOversFilter, setMinOversFilter] = useState();

    const maxPlayerValue = Math.ceil(
        playerData.reduce((max, player) => Math.max(max, player.adjustedValue || 0), 0)
    );

    const filteredPlayerData = playerData.filter(player =>
        (positionFilter.length === 0 || positionFilter.includes(player.positionName)) &&
        (! maxValueFilter || player.adjustedValue <= maxValueFilter) &&
        (! minInningsFilter || (player?.[summaryKey]?.innings || 0) >= minInningsFilter) &&
        (! minOversFilter || (player?.[summaryKey]?.oversBowled || 0) >= minOversFilter)
    );

    const changePositionFilter = (value) => {
        setPositionFilter(value);
        setCurrentPage(1);
    }

    const changeMaxValueFilter = (value) => {
        setMaxValueFilter(value);
        setCurrentPage(1);
    }

    const changeMinInningsFilter = (value) => {
        setMinInningsFilter(value);
        setCurrentPage(1);
    }

    const changeMinOversFilter = (value) => {
        setMinOversFilter(value);
        setCurrentPage(1);
    }

    const resetFilters = () => {
        setVisibleFields(getDefaultVisibleFields());
        setPositionFilter([]);
        setMaxValueFilter(undefined);
        setMinInningsFilter(undefined);
        setMinOversFilter(undefined);
        setCurrentPage(1);
    }

    const calculateColumns = () => {
        
        let columns = [
            {
                title: '',
                key: 'rank',
                render: (text, record, index) => {
                    const currentViewingPage = currentPage -1;
                    return index + 1 + (currentViewingPage*pageSize);
                },
                width: 30
            },
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
                render: (text, record) =>  {
                    return (
                        <Flex align="center" gap={8}>
                            {adminData.sportIsCricket() && record.positionId && (
                                <img src={`/images/${record.positionId}.svg`} width="20px" height="20px" />
                            )}
                            {
                                showPlayersAsLinks ?
                                    <Link to={`/player/${record.id}`}>{text} {record.teamOfTheWeek && <StarFilled style={{marginLeft: 0, color: token.colorStar }} />}</Link> :
                                    <>{text} {record.teamOfTheWeek && <StarFilled style={{marginLeft: 0, color: token.colorStar }} />}</>
                            }
                        </Flex>
                    )
                },
                fixed: true,
                width: 200
            },
            {
                title: 'Value',
                dataIndex: 'adjustedValue',
                key: 'value',
                render: (text) => (`${adminData.currency()}${text}m`),
                sorter: size !== 'small' ? (a, b) => a.adjustedValue - b.adjustedValue : false,
                hidden: ! visibleFields.includes('value')
            },
            {
                title: 'Sel',
                dataIndex: 'selectedBy',
                key: 'selectedBy',
                render: (text, record) => (`${record.selectedBy}%`),
                sorter: size !== 'small' ? (a, b) => a.selectedBy - b.selectedBy : false,
                hidden: ! visibleFields.includes('selectedBy'),
            }
        ];

        if (size !== 'small') {
            userDisplayFields.forEach(item => {

                const columnItem = {
                    title: item.labelPlural, // Not Out
                    dataIndex: item.name, // notOut
                    key: item.name,
                    render: (text, record) => record?.[summaryKey]?.[item.name] || 0,
                    sorter: size !== 'small' ? (a, b) => a?.[summaryKey]?.[item.name] - b?.[summaryKey]?.[item.name] : false,
                    sortDirections: ['descend', 'ascend', 'descend'],
                    hidden: ! visibleFields.includes(item.name)
                };
    
                columns = [...columns, columnItem];
            });
        }

        columns = [
            ...columns,
            {
                title: 'Week',
                dataIndex: 'weekPoints',
                key: 'weekPoints',
                render: (text, record) => record.weekPoints ? record.weekPoints : 0,
                sorter: size !== 'small' ? (a, b) => a.weekPoints - b.weekPoints : false,
                sortDirections: ['descend', 'ascend', 'descend'],
                hidden: ! visibleFields.includes('weekPoints')
            },
            {
                title: 'Total',
                dataIndex: 'totalPoints',
                key: 'totalPoints',
                render: (text, record) => record.totalPoints ? record.totalPoints : 0,
                sorter: size !== 'small' ? (a, b) => a.totalPoints - b.totalPoints : false,
                sortDirections: ['descend', 'ascend', 'descend'],
                defaultSortOrder: 'descend',
                hidden: ! visibleFields.includes('totalPoints')
            }
        ]

        setColumns(columns);
    }

    useEffect(() => {
        calculateColumns();
    }, [showPlayersAsLinks, summaryKey, visibleFields, currentPage, pageSize]);

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
    }

    if (isLoading) {
        return <Loading />
    }

    return (
        <>
            {size !== 'small' && (
                <Row justify="end" style={{ marginBottom: 16 }}>
                    <Col>
                        <Button icon={<FilterOutlined />} onClick={() => setFiltersAreOpen(true)}>
                            Filters
                        </Button>
                    </Col>
                </Row>
            )}

            <Table
                columns={columns}
                dataSource={filteredPlayerData}
                rowKey={record => record.id}
                size={size}
                pagination={size === 'small' ? false : {
                    pageSizeOptions: [10, 25, 50, 100],
                    showSizeChanger: true,
                    hideOnSinglePage: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} players`,
                    pageSize: pageSize,
                    current: currentPage,
                    onChange: handlePageChange
                }}
                scroll={{ x: 'max-content' }}
                loading={isLoading}
                showSorterTooltip={false}
                sticky={true}
                />

            <Modal
                title="Filters"
                open={filtersAreOpen}
                onCancel={() => setFiltersAreOpen(false)}
                footer={[
                    <Button key="reset" onClick={resetFilters}>
                        Reset
                    </Button>,
                    <Button key="close" type="primary" onClick={() => setFiltersAreOpen(false)}>
                        Close
                    </Button>
                ]}
                >
                <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                    <Col xs={24} sm={14}>
                        <Text type="secondary">Positions</Text>
                        <Select
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            placeholder="All Positions"
                            style={{ width: '100%' }}
                            value={positionFilter}
                            onChange={changePositionFilter}
                            options={positions.map(positionName => ({ label: positionName, value: positionName }))}
                            />
                    </Col>
                    <Col xs={24} sm={10}>
                        <Text type="secondary">Max Value</Text>
                        <Select
                            allowClear
                            placeholder="Any Value"
                            style={{ width: '100%' }}
                            value={maxValueFilter}
                            onChange={changeMaxValueFilter}
                            options={Array.from({ length: maxPlayerValue }, (_, index) => maxPlayerValue - index)
                                .map(value => ({ label: `${adminData.currency()}${value}m`, value }))}
                            />
                    </Col>
                </Row>

                {adminData.sportIsCricket() && userData?.isPremium && (
                    <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                        <Col xs={24} sm={12}>
                            <Text type="secondary">Min Innings</Text>
                            <InputNumber
                                min={0}
                                placeholder="Any"
                                style={{ width: '100%' }}
                                value={minInningsFilter}
                                onChange={changeMinInningsFilter}
                                />
                        </Col>
                        <Col xs={24} sm={12}>
                            <Text type="secondary">Min Overs</Text>
                            <InputNumber
                                min={0}
                                placeholder="Any"
                                style={{ width: '100%' }}
                                value={minOversFilter}
                                onChange={changeMinOversFilter}
                                />
                        </Col>
                    </Row>
                )}

                {userData?.isPremium && (
                    <>
                        <Divider />
                        <Text type="secondary">Columns</Text>
                        <Checkbox.Group
                            value={visibleFields}
                            onChange={(checkedValues) => setVisibleFields(checkedValues)}
                            style={{ width: '100%', marginTop: 8, marginBottom: 16 }}
                            >
                            <Row gutter={[8, 8]}>
                                {displayFields.map(field => (
                                    <Col xs={24} sm={12} key={field.name}>
                                        <Checkbox value={field.name}>{field.label}</Checkbox>
                                    </Col>
                                ))}
                            </Row>
                        </Checkbox.Group>
                    </>
                )}
            </Modal>
        </>

    )
}

Playerlist.defaultProps = {
    size: 'middle',
    tabPosition: 'top',
    isLoading: false,
}

Playerlist.propTypes = {
    size: PropTypes.oneOf(['middle', 'small']),
    tabPosition: PropTypes.oneOf(['top', 'left', 'right']),
    playerData: PropTypes.array.isRequired,
    isLoading: PropTypes.bool
}

export default Playerlist;